import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  FilterField,
  FilterRule,
  OPERATORS,
  isValidRule,
} from '../filterRules';

interface Props {
  open: boolean;
  fields: FilterField[];
  onAdd: (rule: FilterRule) => void;
  onClose: () => void;
}

// Builds one rule against any column the table is currently showing. Kept
// deliberately small — rules are added one at a time and then managed as chips
// on the dashboard, so this dialog never has to represent the whole filter set.
export default function FilterBuilder({ open, fields, onAdd, onClose }: Props) {
  const [field, setField] = React.useState('');
  const [operator, setOperator] = React.useState('');
  const [value, setValue] = React.useState('');

  const selected = fields.find(item => item.field === field);
  const operators = selected ? OPERATORS[selected.type] : [];

  // Reset to a sane starting point each time it opens, and whenever the field
  // changes to one whose operators don't include the current choice.
  React.useEffect(() => {
    if (!open) return;
    const first = fields[0];
    setField(current => (fields.some(f => f.field === current) ? current : first?.field || ''));
    setValue('');
  }, [open, fields]);

  React.useEffect(() => {
    if (!selected) return;
    setOperator(current =>
      OPERATORS[selected.type].some(op => op.value === current)
        ? current
        : OPERATORS[selected.type][0].value,
    );
  }, [selected]);

  const rule: FilterRule = { field, operator, value };
  const valid = isValidRule(rule, fields);

  const submit = () => {
    if (!valid) return;
    onAdd(rule);
    setValue('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 18 }}>Add filter</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Field"
            value={field}
            onChange={event => setField(event.target.value)}
            sx={{ minWidth: 200, flex: 1 }}
          >
            {fields.map(item => (
              <MenuItem key={item.field} value={item.field}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Is"
            value={operator}
            onChange={event => setOperator(event.target.value)}
            sx={{ minWidth: 150 }}
          >
            {operators.map(item => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Value"
            type={
              selected?.type === 'number'
                ? 'number'
                : selected?.type === 'date'
                ? 'date'
                : 'text'
            }
            InputLabelProps={selected?.type === 'date' ? { shrink: true } : undefined}
            value={value}
            onChange={event => setValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') submit();
            }}
            sx={{ minWidth: 170, flex: 1 }}
          />
        </Box>

        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 2 }}>
          Filters apply to the rows already loaded for this date range, and are
          carried in the page URL so a filtered view can be shared. Any column in
          the table can be filtered — including ones you have hidden.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={submit} disabled={!valid}>
          Add filter
        </Button>
      </DialogActions>
    </Dialog>
  );
}
